// @ts-strict-ignore
import { DimensionElement } from '@3d-scene/layer/elements/dimension-element';
import { ILayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { Material, Mesh, MeshBasicMaterial, Object3D, SphereGeometry, Vector3 } from 'three';
import { SimpleTextValueVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { LabelElement } from '@3d-scene/layer/elements/label-element';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { SceneManager } from '../../manager/scene.manager';

export class DrillingHoleDimensionElement extends DimensionElement implements ILayerElement {
  public static readonly POINTER_LENGTH: number = 75;
  public static readonly POINT_RADIUS: number = 17;
  public static readonly POINT_LABEL_SPACING: number = 12;
  public static readonly EPSILON: number = 1;

  protected pointerLine: Object3D;
  protected pointerStartPoint: Vector3;
  protected pointerEndPoint: Vector3;

  protected additionalLine: Object3D;
  protected additionalLineStartPoint: Vector3;
  protected additionalLineEndPoint: Vector3;

  protected pointerRequired: boolean;
  protected pointerLength: number;
  protected pointerText: TextValueVO;

  protected pointerOnRight: boolean = true;
  protected updatePointerLine: boolean = true;

  constructor(sceneManager: SceneManager, points: Vector3[], text: TextValueVO, material: Material, level: number = 0,
    pointerLength: number = DrillingHoleDimensionElement.POINTER_LENGTH, pointerText: TextValueVO = null,
    levelOffset: number = DimensionElement.LEVEL_OFFSET, fontSize: number = NaN, fontScale: number = NaN,
    arrowMultiplier: number = DimensionElement.ARROW_MULTIPLIER, pointerRequired: boolean = true, pointerOnRight: boolean = true,
    updatePointerLine: boolean = true) {
    super(sceneManager, points, text, material, level, levelOffset, fontSize, fontScale, arrowMultiplier, false, false);
    this.pointerLength = pointerLength;
    this.pointerRequired = pointerRequired;
    this.pointerText = pointerText;
    this.pointerOnRight = pointerOnRight;
    this.updatePointerLine = updatePointerLine;
    this.init();
  }

  protected init(): void {
    super.init();
    this.createPointer();
    this.createPointerLines();
  }

  protected calculatePoints(): void {
    super.calculatePoints();

    this.pointerStartPoint = this.pointerOnRight ? this.bottomPointRight.clone() : this.bottomPointLeft.clone();
    this.pointerEndPoint = new Vector3(this.pointerStartPoint.x, this.pointerStartPoint.y, this.pointerStartPoint.z - this.pointerLength);

    if (!this.pointerOnRight) {
      this.additionalLineStartPoint = this.bottomPointRight.clone();
      this.additionalLineEndPoint = new Vector3(this.bottomPointRight.x, this.bottomPointRight.y,
        this.bottomPointRight.z - this.pointerLength);
    }
  }

  protected createPointer(): void {
    if (!this.pointerRequired) {
      return;
    }

    const circle: Mesh = new Mesh(new SphereGeometry(DrillingHoleDimensionElement.POINT_RADIUS, 32, 32));
    const circlePosition = this.pointerEndPoint.clone().add(new Vector3(0, 0, -DrillingHoleDimensionElement.POINT_RADIUS / 2));
    circle.position.set(circlePosition.x, circlePosition.y, circlePosition.z);
    circle.material = new MeshBasicMaterial({color: DimensionElement.LINE_COLOR});
    this.add(circle);

    if (this.pointerText) {
      const label: LabelElement = new LabelElement(this.pointerText, this.fontSize);
      const labelPosition = new Vector3(circle.position.x, circle.position.y, circle.position.z -
        DrillingHoleDimensionElement.POINT_RADIUS * 2 - DrillingHoleDimensionElement.POINT_LABEL_SPACING);
      label.position.set(labelPosition.x, labelPosition.y, labelPosition.z);
      this.add(label);
    }
  }

  protected createPointerLines(): void {
    if (!this.updatePointerLine) {
      return;
    }

    this.pointerLine = new Object3D();
    this.pointerLine.add(DimensionUtils.createLineSegment(this.pointerStartPoint, this.pointerEndPoint, DimensionElement.LINE_COLOR));
    this.add(this.pointerLine);

    if (!this.pointerOnRight) {
      this.additionalLine = new Object3D();
      this.additionalLine.add(DimensionUtils.createLineSegment(this.additionalLineStartPoint, this.additionalLineEndPoint,
        DimensionElement.LINE_COLOR));
      this.add(this.additionalLine);
    }
  }

  protected createArrowLine(): void {
    this.arrowLines = new Object3D();

    this.textWidth = DimensionUtils.getTextWidth(this.text as SimpleTextValueVO);

    this.createArrow();

    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointLeft, this.arrowPointRight, DimensionElement.LINE_COLOR));

    this.add(this.arrowLines);
  }

  protected createArrow(): void {
    const arrowLength: number = DimensionElement.ARROW_LENGTH;
    const arrowRadius: number = arrowLength * Math.tan(DimensionElement.ARROW_ANGLE * MathUtils.TO_RADIANS * .5);

    const p1Right: Vector3 = new Vector3(this.arrowPointRight.x + arrowLength, this.arrowPointRight.y,
      this.arrowPointRight.z + arrowRadius);
    const p2Right: Vector3 = new Vector3(this.arrowPointRight.x + arrowLength, this.arrowPointRight.y,
      this.arrowPointRight.z - arrowRadius);
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointRight, p1Right, DimensionElement.LINE_COLOR));
    this.arrowLines.add(DimensionUtils.createLineSegment(this.arrowPointRight, p2Right, DimensionElement.LINE_COLOR));
    // this face is necessary to fill the space between lines
    this.add(FaceUtils.createSingleFaceMesh(p2Right, p1Right, this.arrowPointRight, this.material));
  }

  protected getLabelPosition(): Vector3 {
    return new Vector3(this.arrowPointRight.x + this.textWidth / 2 + DimensionElement.LABEL_OFFSET,
      (this.arrowPointLeft.y + this.arrowPointRight.y) / 2, this.getLabelZPosition());
  }
}
